import { createRoute, Link } from '@tanstack/react-router';
import { useState, useMemo } from 'react';
import { LayoutGrid, List, X, Eye, Heart, User } from 'lucide-react';
import { CONDITIONS } from '@siliconvault/shared';
import { Shell } from '../components/layout/Shell';
import { ItemCard } from '../components/inventory/ItemCard';
import { ItemTable } from '../components/inventory/ItemTable';
import { SkeletonItemCard } from '../components/ui/Skeleton';
import { Input, Select } from '../components/ui/Input';
import { ComboBox } from '../components/ui/ComboBox';
import { useShowroomList } from '../hooks/useInventory';
import { usePreferences } from '../hooks/usePreferences';
import { useAuth } from '../hooks/useAuth';
import { useVault } from '../context/VaultContext';
import { usePlatformLabel } from '../hooks/usePlatformLabel';
import { useTranslation } from '../i18n';
import { useDebounce } from '../hooks/useDebounce';
import { useTaxonomySuggestions } from '../hooks/useTaxonomy';
import { Route as rootRoute } from './__root';
import styles from './showroom.module.css';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/showroom',
  component: ShowroomPage,
});

function getPageNumbers(current: number, total: number): Array<number | '...'> {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  if (current <= 4) return [1, 2, 3, 4, 5, '...', total];
  if (current >= total - 3) return [1, '...', total - 4, total - 3, total - 2, total - 1, total];
  return [1, '...', current - 1, current, current + 1, '...', total];
}

function ShowroomPage() {
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const { preferences } = usePreferences();
  const [view, setView] = useState<'grid' | 'table'>(preferences.defaultView);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [platform, setPlatform] = useState('');
  const [subcategory, setSubcategory] = useState('');
  const [condition, setCondition] = useState('');
  const [listingFilter, setListingFilter] = useState('all');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [includeMyItems, setIncludeMyItems] = useState(false);

  const debouncedSearch = useDebounce(search, 300);
  const { activeVault } = useVault();
  const platformLabel = usePlatformLabel();

  const { suggestions: categorySuggestions } = useTaxonomySuggestions('category');
  const { suggestions: platformSuggestions } = useTaxonomySuggestions('platform');
  const { suggestions: subcategorySuggestions } = useTaxonomySuggestions('subcategory', category || undefined, platform || undefined);

  const query: Record<string, string> = {
    page: String(page),
    limit: String(Math.max(preferences.itemsPerPage, 20)),
    sortBy,
    sortOrder,
    listingFilter,
  };
  if (activeVault) query.vaultType = activeVault.vaultType;
  if (favoritesOnly) query.favoritesOnly = 'true';
  if (includeMyItems) query.includeMyItems = 'true';
  if (debouncedSearch) query.search = debouncedSearch;
  if (category) query.category = category;
  if (platform) query.platform = platform;
  if (subcategory) query.subcategory = subcategory;
  if (condition) query.condition = condition;

  const { data, isLoading } = useShowroomList(query);

  function handleSort(column: string) {
    if (sortBy === column) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  }

  // Active filter chips
  const activeFilters: Array<{ key: string; label: string; clear: () => void }> = [];
  if (category) activeFilters.push({ key: 'category', label: category, clear: () => { setCategory(''); setPage(1); } });
  if (platform) activeFilters.push({ key: 'platform', label: platform, clear: () => { setPlatform(''); setPage(1); } });
  if (subcategory) activeFilters.push({ key: 'subcategory', label: subcategory, clear: () => { setSubcategory(''); setPage(1); } });
  if (condition) activeFilters.push({ key: 'condition', label: t(`condition.${condition}` as any), clear: () => { setCondition(''); setPage(1); } });

  function clearAll() {
    setCategory(''); setPlatform(''); setSubcategory(''); setCondition(''); setPage(1);
  }

  const pageNumbers = data ? getPageNumbers(page, data.totalPages) : [];

  // Spotlight hero: random item with image, only on page 1 with no filters
  const hasFilters = !!debouncedSearch || !!category || !!platform || !!subcategory || !!condition || favoritesOnly;
  const spotlightItem = useMemo(() => {
    if (page !== 1 || hasFilters || !data?.data.length) return null;
    const withImages = data.data.filter((item) => item.imageUrl);
    if (withImages.length === 0) return null;
    return withImages[Math.floor(Math.random() * withImages.length)];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.data.length, page, hasFilters]);

  return (
    <Shell>
      <div className={styles.page}>
        {/* Spotlight hero */}
        {spotlightItem && (
          <Link
            to="/inventory/$itemId"
            params={{ itemId: spotlightItem.id }}
            search={{ from: 'showroom' }}
            className={styles.hero}
          >
            <img src={spotlightItem.imageUrl!} alt={spotlightItem.name} className={styles.heroImage} />
            <div className={styles.heroOverlay} />
            <div className={styles.heroContent}>
              <div className={styles.heroTitle}>{spotlightItem.name}</div>
              <div className={styles.heroMeta}>
                {spotlightItem.ownerDisplayName && (
                  <span className={styles.heroOwner}>{spotlightItem.ownerDisplayName}</span>
                )}
                <span className={styles.heroBadge}>{spotlightItem.condition}</span>
              </div>
            </div>
          </Link>
        )}

        <div className={styles.header}>
          <h1 className={styles.title}>{t('showroom.title')}</h1>
        </div>

        {/* Listing filter tabs */}
        <div style={{ display: 'flex', gap: '4px', marginBottom: '1rem', background: 'var(--bg-tertiary)', borderRadius: 'var(--radius-sm)', padding: '3px', width: 'fit-content' }}>
          {[
            { key: 'all', label: t('community.filterAll') },
            { key: 'showcase', label: t('community.filterShowcase') },
            { key: 'for-trade', label: t('community.filterForTrade') },
            { key: 'for-sale', label: t('community.filterForSale') },
          ].map((tab) => (
            <button
              key={tab.key}
              style={{
                padding: '0.375rem 0.75rem', border: 'none', borderRadius: '4px',
                background: listingFilter === tab.key ? 'var(--accent)' : 'transparent',
                color: listingFilter === tab.key ? 'var(--text-on-accent)' : 'var(--text-secondary)',
                fontFamily: 'var(--font-family)', fontSize: '0.75rem', fontWeight: 500, cursor: 'pointer',
              }}
              onClick={() => { setListingFilter(tab.key); setPage(1); }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Filter bar */}
        <div className={styles.filters}>
          <div className={styles.filterInput}>
            <Input
              placeholder={t('showroom.searchPlaceholder')}
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            />
          </div>
          <ComboBox
            value={category}
            onChange={(v) => { setCategory(v); setPage(1); }}
            suggestions={categorySuggestions}
            placeholder={t('showroom.allCategories')}
          />
          <ComboBox
            value={platform}
            onChange={(v) => { setPlatform(v); setPage(1); }}
            suggestions={platformSuggestions}
            placeholder={`All ${platformLabel}s`}
          />
          <ComboBox
            value={subcategory}
            onChange={(v) => { setSubcategory(v); setPage(1); }}
            suggestions={subcategorySuggestions}
            placeholder={t('inventory.allSubcategories')}
          />
          <Select value={condition} onChange={(e) => { setCondition(e.target.value); setPage(1); }}>
            <option value="">{t('showroom.allConditions')}</option>
            {CONDITIONS.map((c) => <option key={c} value={c}>{t(`condition.${c}` as any)}</option>)}
          </Select>
          <div className={styles.viewToggle}>
            <button
              className={`${styles.viewBtn} ${includeMyItems ? styles.viewBtnActive : ''}`}
              onClick={() => { setIncludeMyItems((v) => !v); setPage(1); }}
              title={includeMyItems ? t('showroom.hideMyItems') : t('showroom.includeMyItems')}
            >
              <User size={15} strokeWidth={1.75} fill={includeMyItems ? 'currentColor' : 'none'} />
            </button>
            <button
              className={`${styles.viewBtn} ${favoritesOnly ? styles.viewBtnActive : ''}`}
              onClick={() => { setFavoritesOnly((v) => !v); setPage(1); }}
              title={favoritesOnly ? t('showroom.showAll') : t('showroom.favoritesOnly')}
            >
              <Heart size={15} strokeWidth={1.75} fill={favoritesOnly ? 'currentColor' : 'none'} />
            </button>
            <button
              className={`${styles.viewBtn} ${view === 'grid' ? styles.viewBtnActive : ''}`}
              onClick={() => setView('grid')}
              title={t('inventory.gridView')}
            >
              <LayoutGrid size={15} strokeWidth={1.75} />
            </button>
            <button
              className={`${styles.viewBtn} ${view === 'table' ? styles.viewBtnActive : ''}`}
              onClick={() => setView('table')}
              title={t('inventory.tableView')}
            >
              <List size={15} strokeWidth={1.75} />
            </button>
          </div>
        </div>

        {/* Active filter chips */}
        {activeFilters.length > 0 && (
          <div className={styles.activeFilters}>
            {activeFilters.map((f) => (
              <span key={f.key} className={styles.filterChip}>
                {f.label}
                <button className={styles.filterChipRemove} onClick={f.clear} aria-label={`Remove ${f.label} filter`}>
                  <X size={11} strokeWidth={2.5} />
                </button>
              </span>
            ))}
            {activeFilters.length >= 2 && (
              <button className={styles.clearAllLink} onClick={clearAll}>{t('showroom.clearAll')}</button>
            )}
            {data && (
              <span className={styles.resultCount}>
                {data.total} {data.total !== 1 ? t('common.results') : t('common.result')}
              </span>
            )}
          </div>
        )}

        {/* Content */}
        {isLoading ? (
          view === 'grid' ? (
            <div className={styles.grid}>
              {Array.from({ length: 12 }).map((_, i) => (
                <SkeletonItemCard key={i} />
              ))}
            </div>
          ) : (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.8125rem' }}>
              {t('showroom.loadingShowroom')}
            </div>
          )
        ) : view === 'grid' ? (
          <div className={styles.grid}>
            {data?.data.map((item) => (
              <ItemCard key={item.id} item={item} showOwner from="showroom" />
            ))}
            {data?.data.length === 0 && (
              <div style={{ gridColumn: '1 / -1' }}>
                <div className={styles.emptyState}>
                  <span className={styles.emptyIcon}><Eye size={56} strokeWidth={1} /></span>
                  <div className={styles.emptyTitle}>
                    {search || category || platform || subcategory || condition ? t('showroom.noMatch') : t('showroom.emptyTitle')}
                  </div>
                  <div className={styles.emptyDesc}>
                    {activeFilters.length ? t('showroom.noMatchDesc')
                      : t('showroom.emptyDesc')}
                  </div>
                  {activeFilters.length > 0 && (
                    <button className={styles.clearAllLink} onClick={clearAll} style={{ fontSize: '0.8125rem' }}>
                      {t('showroom.clearFilters')}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        ) : (
          <ItemTable
            items={data?.data ?? []}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
            showOwner
            from="showroom"
          />
        )}

        {/* Pagination */}
        {data && data.totalPages > 1 && (
          <div className={styles.pagination}>
            <button
              className={styles.pageBtn}
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              ←
            </button>
            {pageNumbers.map((n, i) =>
              n === '...' ? (
                <span key={`ellipsis-${i}`} className={styles.pageEllipsis}>...</span>
              ) : (
                <button
                  key={n}
                  className={`${styles.pageBtn} ${n === page ? styles.pageBtnActive : ''}`}
                  onClick={() => setPage(n as number)}
                >
                  {n}
                </button>
              )
            )}
            <button
              className={styles.pageBtn}
              disabled={page >= data.totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              →
            </button>
          </div>
        )}
      </div>
    </Shell>
  );
}
